<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcTemplate',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    item: {
      type: Object,
      required: true,
    },
    context: {
      type: Object,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const labelAlign = ref('left');
  const objectTypes = ref(null);
  const templateTypes = ref(null);
  const sampleDataSearchResult = ref(null);
  const previewData = ref(null);
  const emailAddress = ref(null);
  const ux = ref([]);
  const b_4892266742011185_style = computed(() => ({
    width: '20px',
  }));
  const b_5970972589163186_options = computed(() => ({
    lineNumbers: true,
    matchBrackets: true,
    continueComments: 'Enter',
    extraKeys: {
      'Ctrl-Q': 'toggleComment',
    },
  }));
  const b_785371160721131_style = computed(() => ({
    width: '20px',
  }));
  const b_253425746720918_options = computed(() => ({
    lineNumbers: true,
    matchBrackets: true,
    continueComments: 'Enter',
    extraKeys: {
      'Ctrl-Q': 'toggleComment',
    },
  }));
  const b_2748386552601189_style = computed(() => ({
    width: '20px',
  }));
  const b_1712041292317581_options = computed(() => ({
    lineNumbers: true,
    matchBrackets: true,
    continueComments: 'Enter',
    extraKeys: {
      'Ctrl-Q': 'toggleComment',
    },
  }));
  const b_539880003873724_style = computed(() => ({
    width: '20px',
  }));
  const b_6324823719113312_options = computed(() => ({
    lineNumbers: true,
    matchBrackets: true,
    continueComments: 'Enter',
    extraKeys: {
      'Ctrl-Q': 'toggleComment',
    },
  }));
  const b_219110658038496_style = computed(() => ({
    width: '20px',
  }));
  const b_3034545286442485_options = computed(() => ({
    lineNumbers: true,
    matchBrackets: true,
    continueComments: 'Enter',
    extraKeys: {
      'Ctrl-Q': 'toggleComment',
    },
  }));
  const b_4556014673857397_style = computed(() => ({
    width: '20px',
  }));
  const b_7091621182964535_options = computed(() => ({
    lineNumbers: true,
    matchBrackets: true,
    continueComments: 'Enter',
    extraKeys: {
      'Ctrl-Q': 'toggleComment',
    },
  }));

  const $data = computed(() => ({
    labelAlign: labelAlign.value,
    objectTypes: objectTypes.value,
    templateTypes: templateTypes.value,
    sampleDataSearchResult: sampleDataSearchResult.value,
    previewData: previewData.value,
    emailAddress: emailAddress.value,
    ux: ux.value,
    b_4892266742011185_style: b_4892266742011185_style.value,
    b_5970972589163186_options: b_5970972589163186_options.value,
    b_785371160721131_style: b_785371160721131_style.value,
    b_253425746720918_options: b_253425746720918_options.value,
    b_2748386552601189_style: b_2748386552601189_style.value,
    b_1712041292317581_options: b_1712041292317581_options.value,
    b_539880003873724_style: b_539880003873724_style.value,
    b_6324823719113312_options: b_6324823719113312_options.value,
    b_219110658038496_style: b_219110658038496_style.value,
    b_3034545286442485_options: b_3034545286442485_options.value,
    b_4556014673857397_style: b_4556014673857397_style.value,
    b_7091621182964535_options: b_7091621182964535_options.value,
  }));

  const $$load_objectTypes = async () => {
    return $fn.value.getEnumValuesFromSchema('ObjectTypes');
  };
  const $$load_templateTypes = async () => {
    return $fn.value.getEnumValuesFromSchema('TemplateTypes');
  };
  const $$load_emailAddress = async () => {
    return $app.value.auth.user.email;
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'labelAlign' },
      { name: 'objectTypes' },
      { name: 'templateTypes' },
      { name: 'sampleDataSearchResult' },
      { name: 'previewData' },
      {
        name: 'emailAddress',
        cache: { type: 'local', key: 'Template.emailAddress' },
      },
      { name: 'ux' },
      { name: 'b_4892266742011185_style', nodeId: '4892266742011185' },
      { name: 'b_5970972589163186_options', nodeId: '5970972589163186' },
      { name: 'b_785371160721131_style', nodeId: '785371160721131' },
      { name: 'b_253425746720918_options', nodeId: '253425746720918' },
      { name: 'b_2748386552601189_style', nodeId: '2748386552601189' },
      { name: 'b_1712041292317581_options', nodeId: '1712041292317581' },
      { name: 'b_539880003873724_style', nodeId: '539880003873724' },
      { name: 'b_6324823719113312_options', nodeId: '6324823719113312' },
      { name: 'b_219110658038496_style', nodeId: '219110658038496' },
      { name: 'b_3034545286442485_options', nodeId: '3034545286442485' },
      { name: 'b_4556014673857397_style', nodeId: '4556014673857397' },
      { name: 'b_7091621182964535_options', nodeId: '7091621182964535' },
    ],
    loaders: {
      objectTypes: $$load_objectTypes,
      templateTypes: $$load_templateTypes,
      emailAddress: $$load_emailAddress,
    },
    ux: {},
    dataRefs: {
      labelAlign,
      objectTypes,
      templateTypes,
      sampleDataSearchResult,
      previewData,
      emailAddress,
      ux,
      b_4892266742011185_style,
      b_5970972589163186_options,
      b_785371160721131_style,
      b_253425746720918_options,
      b_2748386552601189_style,
      b_1712041292317581_options,
      b_539880003873724_style,
      b_6324823719113312_options,
      b_219110658038496_style,
      b_3034545286442485_options,
      b_4556014673857397_style,
      b_7091621182964535_options,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );
  watch(
    () => props.context,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('context');
    }
  );

  const act_7315092382398562_reloadComponentData_159dweedc =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_oqrbni1zv =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_159dweedc,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_oqrbni1zv],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_oqrbni1zv,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_reload_oqrbni1zv = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_oqrbni1zv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div class="overflow-auto">
      <ItpFormGroup
        :label-cols-md="2"
        :content-cols-md="8"
        name="name"
        :label="pgettext('Template', 'Name')"
        :labelAlign="labelAlign"
      >
        <ItpFormInput
          name="name"
          v-model="item.name"
          :value="item.name"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (item.name =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
          required
        >
        </ItpFormInput>
      </ItpFormGroup>
      <ItpFormGroup
        :label-cols-md="2"
        :content-cols-md="8"
        name="object_type"
        :label="pgettext('Template', 'Object Type')"
        :labelAlign="labelAlign"
      >
        <ItpFormSelect2
          name="object_type"
          v-model="item.object_type"
          :value="item.object_type"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (item.object_type =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
          :options="objectTypes"
          required
        >
        </ItpFormSelect2>
      </ItpFormGroup>
      <ItpFormGroup
        :label-cols-md="2"
        :content-cols-md="8"
        name="template_type"
        :label="pgettext('Template', 'Template Type')"
        :labelAlign="labelAlign"
      >
        <ItpFormSelect2
          name="template_type"
          v-model="item.template_type"
          :value="item.template_type"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (item.template_type =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
          :options="templateTypes"
          required
        >
        </ItpFormSelect2>
      </ItpFormGroup>
      <hr :style="b_4892266742011185_style" />
      <ItpText :text="pgettext('Template', 'Subject')" strong> </ItpText>
      <ItpCodeMirror
        mode="html"
        :options="b_5970972589163186_options"
        v-model="item.subject"
        :value="item.subject"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (item.subject =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        class="border"
      >
      </ItpCodeMirror>
      <hr :style="b_785371160721131_style" />
      <ItpText :text="pgettext('Template', 'Header')" strong> </ItpText>
      <ItpCodeMirror
        mode="html"
        :options="b_253425746720918_options"
        v-model="item.header"
        :value="item.header"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (item.header =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        class="border"
      >
      </ItpCodeMirror>
      <hr :style="b_2748386552601189_style" />
      <ItpText :text="pgettext('Template', 'Content')" strong> </ItpText>
      <ItpCodeMirror
        mode="html"
        :options="b_1712041292317581_options"
        v-model="item.content"
        :value="item.content"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (item.content =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        class="border"
      >
      </ItpCodeMirror>
      <hr :style="b_539880003873724_style" />
      <ItpText :text="pgettext('Template', 'Footer')" strong> </ItpText>
      <ItpCodeMirror
        mode="html"
        :options="b_6324823719113312_options"
        v-model="item.footer"
        :value="item.footer"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (item.footer =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        class="border"
      >
      </ItpCodeMirror>
      <hr :style="b_219110658038496_style" />
      <ItpText :text="pgettext('Template', 'CSS')" strong> </ItpText>
      <ItpCodeMirror
        mode="css"
        :options="b_3034545286442485_options"
        v-model="item.css"
        :value="item.css"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (item.css =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        class="border"
      >
      </ItpCodeMirror>
      <hr :style="b_4556014673857397_style" />
      <ItpFormGroup
        name="sampleData"
        :label="pgettext('Template', 'Sample data')"
        required
        :helpTooltip="
          pgettext('Template', 'Enter a valid JSON object, e.g. {}')
        "
      >
        <ItpCodeMirror
          mode="json"
          :options="b_7091621182964535_options"
          v-model="context.sampleData"
          :value="context.sampleData"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (context.sampleData =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
          class="border"
        >
        </ItpCodeMirror>
      </ItpFormGroup>
    </div>
  </ItpUsercontrol>
</template>
